import { useEffect, useState } from "react";
import { Plus, Search, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCountry } from "@/lib/CountryContext";
import Drawer from "@/components/Drawer";
import ActivityTimeline from "@/components/ActivityTimeline";
import FileManager from "@/components/FileManager";

const SECTORS = [
  "cement", "ready_mix", "water_treatment", "wastewater",
  "waste_to_energy", "desalination", "mining", "industrial",
  "pulp_paper", "food_processing", "flue_gas", "other",
];
const EMIRATES = ["AbuDhabi", "Dubai", "Sharjah", "RAK", "Ajman", "UAQ", "Fujairah",
  "Riyadh", "Jazan", "Najran", "EasternProvince", "Tabuk", "Makkah", "Qassim",
  "Doha"];
const STATUSES = ["prospect", "lead", "qualified", "client", "inactive"];

const STATUS_STYLE = {
  prospect: "border-gray-300 text-gray-600 bg-gray-50",
  lead: "border-[#2563EB] text-[#2563EB] bg-blue-50",
  qualified: "border-[#D97706] text-[#D97706] bg-amber-50",
  client: "border-[#059669] text-[#059669] bg-emerald-50",
  inactive: "border-gray-300 text-gray-400 bg-gray-50",
};

function emptyForm(country = "UAE") {
  return {
    name: "", sector: "cement", country, emirate: "", city: "",
    website: "", phone: "", email: "", contact_name: "", contact_role: "",
    status: "prospect", notes: "", lat: null, lng: null,
  };
}

export default function Companies() {
  const { country } = useCountry();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [sectorFilter, setSectorFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [active, setActive] = useState(null); // currently viewed company
  const [tab, setTab] = useState("info");

  const load = async () => {
    const params = { country };
    if (q) params.q = q;
    const r = await api.get("/companies", { params });
    setItems(r.data);
  };

  useEffect(() => { load(); }, [country]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line

  const filtered = sectorFilter === "all"
    ? items
    : items.filter((i) => i.sector === sectorFilter);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm(country));
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm(country), ...item });
    setShowModal(true);
  };

  const openDetail = (item) => {
    setActive(item);
    setTab("info");
    setDrawerOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      payload.lat = payload.lat ? parseFloat(payload.lat) : null;
      payload.lng = payload.lng ? parseFloat(payload.lng) : null;
      if (editing) {
        await api.put(`/companies/${editing.id}`, payload);
        toast.success("Company updated");
      } else {
        await api.post("/companies", payload);
        toast.success("Company created");
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this company?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="companies-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow">CRM</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Companies</h1>
          <p className="text-sm text-gray-500 mt-2">{filtered.length} records</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-company-button">
          <Plus size={16} /> New Company
        </button>
      </div>

      <div className="section-card mb-4 p-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            data-testid="search-companies-input"
            className="input-stark pl-9"
          />
        </div>
        <select
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value)}
          className="input-stark max-w-[180px]"
          data-testid="filter-sector-select"
        >
          <option value="all">All sectors</option>
          {SECTORS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
      </div>

      <div className="section-card overflow-hidden">
        <div className="overflow-x-auto">
        <table className="stark-table" data-testid="companies-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Sector</th>
              <th>Emirate</th>
              <th>Status</th>
              <th>Contact</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} onClick={() => openDetail(c)} className="cursor-pointer">
                <td>
                  <div className="font-semibold">{c.name}</div>
                  {c.website && (
                    <a href={c.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs text-[#004B87] hover:underline">
                      {c.website.replace(/https?:\/\//, "")}
                    </a>
                  )}
                </td>
                <td className="text-gray-600 capitalize">{c.sector.replace(/_/g, " ")}</td>
                <td className="text-gray-600">{c.emirate || "—"}</td>
                <td>
                  <span className={`chip ${STATUS_STYLE[c.status] || ""}`}>
                    {c.status}
                  </span>
                </td>
                <td className="text-gray-600">
                  {c.contact_name ? (
                    <>
                      <div>{c.contact_name}</div>
                      {c.contact_role && <div className="text-xs text-gray-400">{c.contact_role}</div>}
                    </>
                  ) : "—"}
                </td>
                <td className="text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); openEdit(c); }}
                    className="text-gray-400 hover:text-[#004B87] mr-3"
                    data-testid={`edit-company-${c.id}`}
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                    className="text-gray-400 hover:text-red-600"
                    data-testid={`delete-company-${c.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-gray-400 py-12">No companies found</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        eyebrow="Company"
        title={active?.name || ""}
        testid="company-drawer"
      >
        {active && (
          <div className="p-6">
            <div className="border-b border-gray-200 mb-6 flex gap-6">
              {[
                { id: "info", label: "Info" },
                { id: "activities", label: "Timeline" },
                { id: "files", label: "Files" },
              ].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`company-tab-${t.id}`}
                  className={`pb-3 text-sm font-semibold transition-colors border-b-2 ${
                    tab === t.id ? "text-[#004B87] border-[#004B87]" : "text-gray-500 border-transparent hover:text-gray-700"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "info" && (
              <div className="space-y-4">
                <Stat label="Sector" value={active.sector?.replace(/_/g, " ")} />
                <Stat label="Location" value={`${active.emirate || ""} ${active.city ? "· " + active.city : ""}`} />
                <Stat label="Status" value={
                  <span className={`chip ${STATUS_STYLE[active.status] || ""}`}>{active.status}</span>
                } />
                <Stat label="Contact" value={active.contact_name ? `${active.contact_name}${active.contact_role ? " · " + active.contact_role : ""}` : "—"} />
                <Stat label="Email" value={active.email || "—"} />
                <Stat label="Phone" value={active.phone || "—"} />
                <Stat label="Website" value={
                  active.website ? <a href={active.website} target="_blank" rel="noreferrer" className="text-[#004B87] hover:underline">{active.website}</a> : "—"
                } />
                {active.notes && (
                  <div>
                    <div className="label-eyebrow mb-2">Notes</div>
                    <div className="text-sm whitespace-pre-wrap">{active.notes}</div>
                  </div>
                )}
                <div className="pt-4 border-t border-gray-200">
                  <button onClick={() => { setDrawerOpen(false); openEdit(active); }} className="btn-ghost" data-testid="drawer-edit-company">
                    <Pencil size={14} /> Edit
                  </button>
                </div>
              </div>
            )}

            {tab === "activities" && <ActivityTimeline entityType="company" entityId={active.id} />}
            {tab === "files" && <FileManager entityType="company" entityId={active.id} />}
          </div>
        )}
      </Drawer>

      {showModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowModal(false)}>
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={save}
            data-testid="company-form-modal"
            className="bg-white border border-gray-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="label-eyebrow">{editing ? "Edit" : "New"}</div>
                <div className="font-display text-xl font-bold mt-1">Company</div>
              </div>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-black">
                <X size={20} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 p-6">
              <Field label="Name" required>
                <input className="input-stark" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="company-name-input" />
              </Field>
              <Field label="Sector">
                <select className="input-stark" value={form.sector}
                  onChange={(e) => setForm({ ...form, sector: e.target.value })}>
                  {SECTORS.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
              <Field label="Country">
                <input className="input-stark" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </Field>
              <Field label="Emirate / Region">
                <select className="input-stark" value={form.emirate || ""}
                  onChange={(e) => setForm({ ...form, emirate: e.target.value })}>
                  <option value="">—</option>
                  {EMIRATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="City">
                <input className="input-stark" value={form.city || ""}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </Field>
              <Field label="Status">
                <select className="input-stark" value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Website">
                <input className="input-stark" value={form.website || ""}
                  onChange={(e) => setForm({ ...form, website: e.target.value })} />
              </Field>
              <Field label="Phone">
                <input className="input-stark" value={form.phone || ""}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </Field>
              <Field label="Email">
                <input type="email" className="input-stark" value={form.email || ""}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label="Contact name">
                <input className="input-stark" value={form.contact_name || ""}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
              </Field>
              <Field label="Contact role">
                <input className="input-stark" value={form.contact_role || ""}
                  onChange={(e) => setForm({ ...form, contact_role: e.target.value })} />
              </Field>
              <Field label="Latitude">
                <input className="input-stark" type="number" step="any" value={form.lat ?? ""}
                  onChange={(e) => setForm({ ...form, lat: e.target.value })} />
              </Field>
              <Field label="Longitude">
                <input className="input-stark" type="number" step="any" value={form.lng ?? ""}
                  onChange={(e) => setForm({ ...form, lng: e.target.value })} />
              </Field>
              <Field label="Notes" full>
                <textarea className="input-stark min-h-[100px]" value={form.notes || ""}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-company-button">
                {editing ? "Save changes" : "Create"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

export function Field({ label, children, required, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="label-eyebrow block mb-2">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  );
}

export function Stat({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-start">
      <div className="label-eyebrow pt-1">{label}</div>
      <div className="col-span-2 text-sm">{value || "—"}</div>
    </div>
  );
}
